import { API_BASE_URL, request } from './request';

export const preparePublishPreflight = async (params = {}) => request(`${API_BASE_URL}/publish/preflight`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const executePublishNow = async (params = {}) => request(`${API_BASE_URL}/publish/execute_now`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const retryPublished = async (params = {}) => request(`${API_BASE_URL}/publish/retry`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const getPublishVerificationInfo = async (params = {}) => request(`${API_BASE_URL}/publish/verification/info`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const submitPublishVerificationSms = async (params = {}) => request(`${API_BASE_URL}/publish/verification/sms`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const cancelPublishVerification = async (params = {}) => request(`${API_BASE_URL}/publish/verification/cancel`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const createPublishTask = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/create`, {
  method: 'POST',
  body: JSON.stringify(params),
});

const defaultTaskListParams = (params = {}) => ({
  page: 1,
  page_size: 20,
  keyword: '',
  filters: {},
  sort: { field: 'created_at', order: 'desc' },
  ...params,
});

export const getPublishTaskList = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/list`, {
  method: 'POST',
  body: JSON.stringify(defaultTaskListParams(params)),
});

export const getPublishTasksList = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/list`, {
  method: 'POST',
  body: JSON.stringify(defaultTaskListParams(params)),
});

export const updatePublishTask = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/update`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const deletePublishTask = async (taskIds) => request(`${API_BASE_URL}/publish/tasks/delete`, {
  method: 'POST',
  body: JSON.stringify({
    task_ids: Array.isArray(taskIds) ? taskIds : [taskIds],
  }),
});

export const nlToSchedule = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/nl_to_schedule`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const getPublishList = async (params = {}) => request(`${API_BASE_URL}/publish/list`, {
  method: 'POST',
  body: JSON.stringify({
    page: 1,
    page_size: 10,
    sort_field: 'publish_time',
    sort_order: 'desc',
    ...params,
  }),
});

export const searchPublishList = async (params = {}) => request(`${API_BASE_URL}/publish/search`, {
  method: 'POST',
  body: JSON.stringify({
    page: 1,
    page_size: 20,
    keyword: '',
    filters: {},
    sort: { field: 'publish_time', order: 'desc' },
    ...params,
  }),
});

export const getPublishDetail = async (params = {}) => request(`${API_BASE_URL}/publish/detail`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const downloadPublishContent = async (publishId) => {
  const url = `${API_BASE_URL}/publish/download`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ publish_id: publishId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && (contentType.includes('application/octet-stream')
      || contentType.includes('application/zip')
      || contentType.includes('application/pdf')
      || contentType.includes('video/')
      || contentType.includes('image/')
      || contentType.includes('text/'))) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `publish_${publishId}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          if (filename.startsWith('UTF-8\'\'')) {
            filename = decodeURIComponent(filename.replace(/^UTF-8''/, ''));
          }
        }
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { status: 'success', message: '下载成功' };
    }
    return await response.json();
  } catch (error) {
    console.error('下载失败:', error);
    throw error;
  }
};

export const deletePublishContent = async (publishId) => batchDeletePublishContent([publishId]);

export const batchDeletePublishContent = async (publishIds) => request(`${API_BASE_URL}/publish/delete`, {
  method: 'POST',
  body: JSON.stringify({
    publish_ids: Array.isArray(publishIds) ? publishIds : [publishIds],
  }),
});

export const triggerPublishTaskOnce = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/trigger_once`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const executePublishTaskOnce = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/execute_once`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const getActivePublishTaskRuns = async (params = {}) => request(`${API_BASE_URL}/publish/tasks/active_runs`, {
  method: 'POST',
  body: JSON.stringify(params),
});

export const getPublishTaskRun = async (taskRunId) => {
  const id = String(taskRunId || '').trim();
  if (!id) throw new Error('taskRunId is required');
  return request(`${API_BASE_URL}/publish/task_runs/${encodeURIComponent(id)}`, { method: 'GET' });
};

export const listPublishJobsByRun = async (taskRunId, params = {}) => {
  const id = String(taskRunId || '').trim();
  if (!id) throw new Error('taskRunId is required');
  return request(`${API_BASE_URL}/publish/task_runs/${encodeURIComponent(id)}/jobs/list`, {
    method: 'POST',
    body: JSON.stringify({ page: 1, page_size: 50, ...params }),
  });
};

export const listPublishTaskRuns = async (params = {}) => request(`${API_BASE_URL}/publish/task_runs/list`, {
  method: 'POST',
  body: JSON.stringify({ page: 1, page_size: 10, ...params }),
});

export const cancelPublishTaskRun = async (taskRunId) => {
  const id = String(taskRunId || '').trim();
  if (!id) throw new Error('taskRunId is required');
  return request(`${API_BASE_URL}/publish/task_runs/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
};

export const retryPublishTaskRun = async (taskRunId, params = {}) => {
  const id = String(taskRunId || '').trim();
  if (!id) throw new Error('taskRunId is required');
  return request(`${API_BASE_URL}/publish/task_runs/${encodeURIComponent(id)}/retry`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};
